import { useCallback, useEffect, useRef, useState } from 'react';

// Shared staging model for the "Extract from Source" intake surfaces (Quotation +
// Sales Order). Owns the list of files a user has attached — whether picked, dropped,
// or PASTED from the clipboard — plus the object-URL lifecycle for image thumbnails.
// Both intake components submit `rawFiles` to the combined /api/intake/extract call
// (86d3hdy52).

export type StagedFileSource = 'upload' | 'paste';

export interface StagedFile {
  id: string;
  file: File;
  source: StagedFileSource;
  isImage: boolean;
  /** Object URL for image previews; undefined for non-image files. Revoked on remove. */
  previewUrl?: string;
}

// The backend intake pipeline only accepts PNG/JPEG among image types, so we filter
// pasted images to the same set (the file picker already restricts to these via accept).
const PASTEABLE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg']);

function makeId(): string {
  // crypto.randomUUID needs a secure context; guard + fall back like intakeExtraction.ts.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sf-${Date.now().toString(36)}-${Math.round(Math.random() * 1e9).toString(36)}`;
}

function mbLabel(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

interface UseStagedFilesOptions {
  /** Per-file size ceiling in bytes. Files above it are skipped via onReject. */
  maxSize: number;
  /** Surface a rejection message (e.g. toast.error). */
  onReject?: (message: string) => void;
}

export function useStagedFiles({ maxSize, onReject }: UseStagedFilesOptions) {
  const [staged, setStaged] = useState<StagedFile[]>([]);
  // Tracks every live object URL so unmount can revoke any that weren't removed.
  const urlsRef = useRef<Set<string>>(new Set());

  const toStaged = useCallback((file: File, source: StagedFileSource): StagedFile => {
    const isImage = file.type.startsWith('image/');
    let previewUrl: string | undefined;
    if (isImage) {
      previewUrl = URL.createObjectURL(file);
      urlsRef.current.add(previewUrl);
    }
    return { id: makeId(), file, source, isImage, previewUrl };
  }, []);

  const addFiles = useCallback(
    (incoming: File[] | FileList, source: StagedFileSource = 'upload') => {
      const accepted: StagedFile[] = [];
      for (const file of Array.from(incoming)) {
        if (file.size > maxSize) {
          onReject?.(`${file.name} exceeds ${mbLabel(maxSize)} — skipped`);
          continue;
        }
        accepted.push(toStaged(file, source));
      }
      if (accepted.length > 0) setStaged((prev) => [...prev, ...accepted]);
    },
    [maxSize, onReject, toStaged],
  );

  // Pull image files off a paste event. Returns true only when it consumed an image —
  // the caller then preventDefault()s so the browser doesn't also paste it as text.
  // Guards:
  //   * If the clipboard carries real text (Excel/HTML cell copies ship BOTH a bitmap
  //     and text), we bail so the normal text paste wins — never hijack those.
  //   * Only PNG/JPEG are staged (matches the backend allow-list); other image types
  //     are rejected with a message.
  const addFromClipboard = useCallback(
    (e: React.ClipboardEvent): boolean => {
      const cd = e.clipboardData;
      if (!cd) return false;
      if (cd.getData('text/plain').trim().length > 0) return false;

      const images: File[] = [];
      let seq = 0;
      for (const item of Array.from(cd.items)) {
        if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
        if (!PASTEABLE_IMAGE_TYPES.has(item.type)) {
          onReject?.(`Pasted image type ${item.type} isn't supported — use PNG or JPEG`);
          continue;
        }
        const blob = item.getAsFile();
        if (!blob) continue;
        seq += 1;
        const ext = item.type === 'image/png' ? 'png' : 'jpg';
        // Clipboard files are usually named "image.png"; give a friendlier unique name.
        images.push(new File([blob], `pasted-image-${seq}.${ext}`, { type: item.type }));
      }

      if (images.length === 0) return false;
      addFiles(images, 'paste');
      return true;
    },
    [addFiles, onReject],
  );

  const removeAt = useCallback((id: string) => {
    setStaged((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        urlsRef.current.delete(target.previewUrl);
      }
      return prev.filter((s) => s.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setStaged((prev) => {
      for (const s of prev) {
        if (s.previewUrl) {
          URL.revokeObjectURL(s.previewUrl);
          urlsRef.current.delete(s.previewUrl);
        }
      }
      return [];
    });
  }, []);

  // Revoke any URLs still alive when the component unmounts (e.g. dialog closed mid-edit).
  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  return {
    staged,
    /** The raw File[] to submit to extraction — order matches `staged`. */
    rawFiles: staged.map((s) => s.file),
    hasFiles: staged.length > 0,
    count: staged.length,
    addFiles,
    addFromClipboard,
    removeAt,
    clear,
  };
}
